/**
 * PO Transaction Helpers
 *
 * Phase 1 — createPendingPOTransaction:
 *   Inserts a transaction record with delivery_status='ordered'.
 *   Does NOT touch inventory items or aggregate tables.
 *
 * Phase 2 — confirmPODelivery:
 *   Updates delivery_status='delivered', sets actual_delivery_date + expiry_date + doc paths,
 *   then creates *_inventory_items and updates aggregate.
 */

import { supabase } from '../../../supabaseClient';
import { getInventoryConfig } from '../../inventory/inventory-components/shared/inventoryConfig';

// ── Storage buckets per inventory type ───────────────────────────────────────

const BUCKETS = {
  caps:      { invoice: 'caps-transactions-invoices',           po: 'caps-transactions-po',           coa: 'caps-transactions-coa',           msds: 'caps-transactions-msds',     bol: 'caps-transactions-bol' },
  bottles:   { invoice: 'bottles-transactions-invoices',        po: 'bottles-transactions-po',        coa: 'bottles-transactions-coa',        msds: 'bottles-transactions-msds',  bol: 'bottles-transactions-bol' },
  labels:    { invoice: 'labels-transactions-invoices',         po: 'labels-transactions-po',         coa: 'labels-transactions-coa',         msds: 'labels-transactions-msds',   bol: 'labels-transactions-bol' },
  supplies:  { invoice: 'supplies-transactions-invoices',       po: 'supplies-transactions-po',       coa: 'supplies-transactions-coa',       msds: 'supplies-transactions-msds', bol: 'supplies-transactions-bol' },
  chemicals: { invoice: 'raw-materials-transactions-invoices',  po: 'raw-material-inventory-po',      coa: 'raw-materials-inventory-coa',     msds: 'raw-materials-inventory-msds',bol: 'raw-material-inventory-bol' },
  products:  { invoice: 'products-inventory-invoice-documents', po: 'products-inventory-po',          coa: 'products-inventory-coa-documents',sds: 'products-inventory-sds-documents', bol: 'products-transactions-bol', production_doc: 'products-inventory-production-documents' },
};

async function uploadDocs(companyId, inventoryType, documents) {
  const bucketMap = BUCKETS[inventoryType] || {};
  const uploads = {};
  const ts = Date.now();
  for (const [key, bucket] of Object.entries(bucketMap)) {
    const file = documents?.[key];
    if (!file) continue;
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${companyId}/${ts}_${safeName}`;
      const { error } = await supabase.storage.from(bucket).upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (!error) uploads[key] = { path: filePath, name: file.name, size: file.size };
    } catch (err) { console.error(`Upload ${key}:`, err); }
  }
  return uploads;
}

function docFields(uploads) {
  const out = {};
  if (uploads.invoice)        { out.invoice_file_path        = uploads.invoice.path;        out.invoice_file_name        = uploads.invoice.name;        out.invoice_file_size        = uploads.invoice.size; }
  if (uploads.po)             { out.po_file_path             = uploads.po.path;             out.po_file_name             = uploads.po.name;             out.po_file_size             = uploads.po.size; }
  if (uploads.coa)            { out.coa_file_path            = uploads.coa.path;            out.coa_file_name            = uploads.coa.name;            out.coa_file_size            = uploads.coa.size; }
  if (uploads.msds)           { out.msds_file_path           = uploads.msds.path;           out.msds_file_name           = uploads.msds.name;           out.msds_file_size           = uploads.msds.size; }
  if (uploads.bol)            { out.bol_file_path            = uploads.bol.path;            out.bol_file_name            = uploads.bol.name;            out.bol_file_size            = uploads.bol.size; }
  if (uploads.sds)            { out.sds_file_path            = uploads.sds.path;            out.sds_file_name            = uploads.sds.name;            out.sds_file_size            = uploads.sds.size; }
  if (uploads.production_doc) { out.production_doc_file_path = uploads.production_doc.path; out.production_doc_file_name = uploads.production_doc.name; out.production_doc_file_size = uploads.production_doc.size; }
  return out;
}

async function updateAggregate(companyId, config, itemId, locationId) {
  const { data: items } = await supabase
    .from(config.itemsTable)
    .select('current_quantity')
    .eq('company_id', companyId)
    .eq(config.itemIdField, itemId)
    .eq('location_id', locationId);

  const total = (items || []).reduce((s, i) => s + (i.current_quantity || 0), 0);
  const { data: agg } = await supabase
    .from(config.inventoryTable)
    .select('*')
    .eq('company_id', companyId)
    .eq(config.itemIdField, itemId)
    .eq('location_id', locationId)
    .maybeSingle();

  const ts = new Date().toISOString();
  if (agg) {
    if (total > 0) {
      await supabase.from(config.inventoryTable).update({ current_quantity: total, [config.lastUpdatedField || 'last_updated']: ts }).eq('id', agg.id);
    } else {
      await supabase.from(config.inventoryTable).delete().eq('id', agg.id);
    }
  } else if (total > 0) {
    await supabase.from(config.inventoryTable).insert([{ company_id: companyId, [config.itemIdField]: itemId, location_id: locationId, current_quantity: total, [config.lastUpdatedField || 'last_updated']: ts }]);
  }
}

// ── Phase 1: Create pending transaction (no inventory change) ─────────────────

export async function createPendingPOTransaction(companyId, inventoryType, config, data) {
  const { item, location, lot, shared, deliveryInfo, variantId } = data;

  // Products use different column names from other inventory types
  if (inventoryType === 'products') {
    const uQty           = parseInt(lot.unitQty)    || 0;
    const cQty           = parseInt(lot.caseQty)    || 0;
    const pQty           = parseInt(lot.palletQty)  || 0;
    const unitsPerCase   = parseInt(lot._unitsPerCase)   || 1;
    const casesPerPallet = parseInt(lot._casesPerPallet) || 1;
    const totalUnits     = uQty + (cQty * unitsPerCase) + (pQty * casesPerPallet * unitsPerCase);
    const productBase = {
      company_id:             companyId,
      product_id:             item.id,
      variant_id:             variantId || null,
      to_location_id:         location.id,
      transaction_type:       'purchase',
      delivery_status:        'ordered',
      quantity_change:        totalUnits,
      input_units:            uQty,
      input_cases:            cQty,
      input_pallets:          pQty,
      units_per_case:         unitsPerCase,
      cases_per_pallet:       casesPerPallet,
      unit_cost:              lot.unitCost ? parseFloat(lot.unitCost) : null,
      supplier_id:            shared.vendor?.id || null,
      invoice_number:         shared.invoiceNumber || null,
      invoice_date:           shared.invoiceDate   || null,
      po_number:              shared.poNumber      || null,
      po_date:                shared.poDate        || null,
      expected_delivery_date: deliveryInfo.expectedDate || null,
      notes:                  deliveryInfo.notes || null,
    };
    const { data: tx, error } = await supabase
      .from(config.transactionsTable)
      .insert([productBase])
      .select()
      .single();
    if (error) throw error;
    return tx;
  }

  const base = {
    company_id:              companyId,
    [config.itemIdField]:    item.id,
    location_id:             location.id,
    transaction_type:        'purchase',
    delivery_status:         'ordered',
    lot_number:              lot.lotNumber   || null,
    batch_number:            lot.batchNumber || null,
    supplier_id:             shared.vendor?.id || null,
    invoice_number:          shared.invoiceNumber || null,
    invoice_date:            shared.invoiceDate   || null,
    po_number:               shared.poNumber      || null,
    po_date:                 shared.poDate        || null,
    expected_delivery_date:  deliveryInfo.expectedDate || null,
    transaction_date:        new Date().toISOString(),
    notes:                   deliveryInfo.notes || null,
  };

  // Chemicals: dual-quantity
  if (inventoryType === 'chemicals') {
    const unitQty  = parseFloat(lot.quantity) || 0;
    const unitSize = parseFloat(item.primary_unit_quantity) || 1;
    Object.assign(base, {
      unit_quantity:  unitQty,
      total_quantity: parseFloat((unitQty * unitSize).toFixed(4)),
      unit_cost:      lot.unitCost ? parseFloat(lot.unitCost) : null,
    });
  } else {
    // Generic (caps/bottles/labels/supplies)
    Object.assign(base, {
      quantity:  parseFloat(lot.quantity) || 0,
      unit_cost: lot.unitCost ? parseFloat(lot.unitCost) : null,
    });
  }

  const { data: tx, error } = await supabase
    .from(config.transactionsTable)
    .insert([base])
    .select()
    .single();

  if (error) throw error;
  return tx;
}

// ── Phase 2: Confirm delivery — update transaction + create inventory items ───

export async function confirmPODelivery(companyId, inventoryType, config, txId, data, lotOptions = {}) {
  const { lotIndex = 0, totalLots = 1, isFullDelivery = true, orderedQty = 0, totalDelivered = 0 } = lotOptions;
  const isFirstLot    = lotIndex === 0;
  const isMultiLot    = totalLots > 1;
  // Partial delivery: delivered qty < ordered qty — keep original as pending, create new delivered tx per lot
  const isPartial     = !isFullDelivery;

  const { location, lot, expiryDate, actualDeliveryDate, documents, taxAmount, shippingCost, invoiceDueDate, invoiceNumber, invoiceDate, variantId } = data;

  // Upload documents only on first lot
  const docUploads = isFirstLot ? await uploadDocs(companyId, inventoryType, documents || {}) : {};

  // Column availability guards
  const hasExpiryColumn = inventoryType === 'chemicals' || inventoryType === 'products';
  // caps and labels use shipping_amount; all other types use shipping_cost
  const shippingColName = ['caps', 'labels'].includes(inventoryType) ? 'shipping_amount' : 'shipping_cost';

  // For additional lots in multi-lot delivery: create a NEW transaction (copy of original)
  // so each lot has its own independent source_transaction_id.
  // This fixes: (a) delete lot 1 wiping lot 2 data, (b) transaction history shows 1 entry per lot.
  let effectiveTxId = txId;
  let copiedDocPaths = {}; // For products: doc paths copied from the first lot's inventory item

  // Partial delivery Lot 1: create a new delivered tx; update original with remaining qty (stays pending)
  if (isFirstLot && isPartial) {
    const { data: origTx } = await supabase
      .from(config.transactionsTable).select('*').eq('id', txId).single();

    if (origTx) {
      // eslint-disable-next-line no-unused-vars
      const { id: _id, created_at: _ca, updated_at: _ua, ...copyFields } = origTx;
      const remainingQty = Math.max(0, orderedQty - totalDelivered);

      // Update original: remaining qty, keep status=pending
      let remainingFields = {};
      if (inventoryType === 'chemicals') {
        const unitSize = parseFloat(data.item?.primary_unit_quantity) || 1;
        remainingFields = { unit_quantity: remainingQty, total_quantity: parseFloat((remainingQty * unitSize).toFixed(4)) };
      } else if (inventoryType !== 'products') {
        remainingFields = { quantity: remainingQty };
      }
      if (Object.keys(remainingFields).length) {
        await supabase.from(config.transactionsTable).update(remainingFields).eq('id', txId);
      }

      // Create a new delivered transaction for this lot
      let deliveredFields = {
        delivery_status:      'delivered',
        actual_delivery_date: actualDeliveryDate || new Date().toISOString().split('T')[0],
        lot_number:           lot.lotNumber   || null,
        batch_number:         lot.batchNumber || null,
        ...(hasExpiryColumn   ? { expiry_date: expiryDate || null } : {}),
        ...(invoiceNumber ? { invoice_number: invoiceNumber } : {}),
        ...(invoiceDate   ? { invoice_date:   invoiceDate   } : {}),
        ...(inventoryType !== 'products' ? docFields(docUploads) : {}),
      };
      if (inventoryType === 'chemicals') {
        const unitQty  = parseFloat(lot.quantity) || 0;
        const unitSize = parseFloat(data.item?.primary_unit_quantity) || 1;
        deliveredFields = { ...deliveredFields, unit_quantity: unitQty, total_quantity: parseFloat((unitQty * unitSize).toFixed(4)) };
      } else if (inventoryType !== 'products') {
        deliveredFields = { ...deliveredFields, quantity: parseFloat(lot.quantity) || 0 };
      }

      const { data: newTx, error: newTxErr } = await supabase
        .from(config.transactionsTable)
        .insert([{ ...copyFields, ...deliveredFields }])
        .select('id').single();
      if (newTxErr) throw newTxErr;
      effectiveTxId = newTx.id;
    }
  }

  if (!isFirstLot && isMultiLot) {
    // Fetch the original transaction to copy its fields
    const { data: origTx } = await supabase
      .from(config.transactionsTable).select('*').eq('id', txId).single();

    if (origTx) {
      // eslint-disable-next-line no-unused-vars
      const { id: _id, created_at: _ca, updated_at: _ua, ...copyFields } = origTx;

      let additionalFields = {
        delivery_status:      'delivered',
        actual_delivery_date: actualDeliveryDate || new Date().toISOString().split('T')[0],
        lot_number:           lot.lotNumber   || null,
        batch_number:         lot.batchNumber || null,
        ...(hasExpiryColumn   ? { expiry_date:   expiryDate || null } : {}),
        ...(invoiceNumber ? { invoice_number: invoiceNumber } : {}),
        ...(invoiceDate   ? { invoice_date:   invoiceDate   } : {}),
      };

      // For products: update unit qty fields for this lot
      if (inventoryType === 'products') {
        const uQty           = parseInt(lot.unitQty)        || 0;
        const cQty           = parseInt(lot.caseQty)        || 0;
        const pQty           = parseInt(lot.palletQty)      || 0;
        const unitsPerCase   = parseInt(lot._unitsPerCase)   || origTx.units_per_case   || 1;
        const casesPerPallet = parseInt(lot._casesPerPallet) || origTx.cases_per_pallet || 1;
        const totalUnits     = uQty + (cQty * unitsPerCase) + (pQty * casesPerPallet * unitsPerCase);
        additionalFields = { ...additionalFields, input_units: uQty, input_cases: cQty, input_pallets: pQty, quantity_change: totalUnits, units_per_case: unitsPerCase, cases_per_pallet: casesPerPallet };
        if (variantId) additionalFields.variant_id = variantId;
      }
      // For chemicals: update quantities for this lot
      else if (inventoryType === 'chemicals') {
        const unitQty  = parseFloat(lot.quantity) || 0;
        const unitSize = parseFloat(data.item?.primary_unit_quantity) || origTx.unit_quantity ? (origTx.total_quantity / origTx.unit_quantity) : 1;
        additionalFields = { ...additionalFields, unit_quantity: unitQty, total_quantity: parseFloat((unitQty * unitSize).toFixed(4)) };
      }
      // For generic (caps/bottles/labels/supplies): update quantity
      else {
        additionalFields = { ...additionalFields, quantity: parseFloat(lot.quantity) || 0 };
      }

      const { data: newTx, error: newTxErr } = await supabase
        .from(config.transactionsTable)
        .insert([{ ...copyFields, ...additionalFields }])
        .select('id').single();
      if (newTxErr) throw newTxErr;
      effectiveTxId = newTx.id;
    }

    // For products: copy doc paths from first lot's inventory item
    if (inventoryType === 'products') {
      const { data: firstItem } = await supabase
        .from('product_inventory_items').select('po_file_path,po_file_name,po_file_size,invoice_file_path,invoice_file_name,invoice_file_size,coa_file_path,coa_file_name,coa_file_size,sds_file_path,sds_file_name,sds_file_size,bol_file_path,bol_file_name,bol_file_size,production_doc_file_path,production_doc_file_name,production_doc_file_size')
        .eq('source_transaction_id', txId).eq('company_id', companyId)
        .order('created_at', { ascending: true }).limit(1).maybeSingle();
      if (firstItem) {
        if (firstItem.po_file_path)             copiedDocPaths = { ...copiedDocPaths, po_file_path: firstItem.po_file_path, po_file_name: firstItem.po_file_name, po_file_size: firstItem.po_file_size };
        if (firstItem.invoice_file_path)        copiedDocPaths = { ...copiedDocPaths, invoice_file_path: firstItem.invoice_file_path, invoice_file_name: firstItem.invoice_file_name, invoice_file_size: firstItem.invoice_file_size };
        if (firstItem.coa_file_path)            copiedDocPaths = { ...copiedDocPaths, coa_file_path: firstItem.coa_file_path, coa_file_name: firstItem.coa_file_name, coa_file_size: firstItem.coa_file_size };
        if (firstItem.sds_file_path)            copiedDocPaths = { ...copiedDocPaths, sds_file_path: firstItem.sds_file_path, sds_file_name: firstItem.sds_file_name, sds_file_size: firstItem.sds_file_size };
        if (firstItem.bol_file_path)            copiedDocPaths = { ...copiedDocPaths, bol_file_path: firstItem.bol_file_path, bol_file_name: firstItem.bol_file_name, bol_file_size: firstItem.bol_file_size };
        if (firstItem.production_doc_file_path) copiedDocPaths = { ...copiedDocPaths, production_doc_file_path: firstItem.production_doc_file_path, production_doc_file_name: firstItem.production_doc_file_name, production_doc_file_size: firstItem.production_doc_file_size };
      }
    }
  }

  // Update ORIGINAL transaction to delivered — only on first lot of a FULL delivery
  // (partial delivery creates a new delivered tx above and keeps original as pending)
  if (isFirstLot && !isPartial) {
    const updatePayload = {
      delivery_status:      'delivered',
      actual_delivery_date: actualDeliveryDate || new Date().toISOString().split('T')[0],
      ...(lot?.lotNumber   !== undefined ? { lot_number:   lot.lotNumber   || null } : {}),
      ...(lot?.batchNumber !== undefined ? { batch_number: lot.batchNumber || null } : {}),
      ...(hasExpiryColumn   ? { expiry_date:   expiryDate   || null } : {}),
      ...(taxAmount != null && taxAmount !== '' ? { tax_amount: parseFloat(taxAmount) || 0 } : {}),
      ...(shippingCost != null && shippingCost !== '' ? { [shippingColName]: parseFloat(shippingCost) || 0 } : {}),
      ...(invoiceDueDate != null && invoiceDueDate !== '' ? { invoice_due_date: invoiceDueDate || null } : {}),
      ...(invoiceNumber ? { invoice_number: invoiceNumber } : {}),
      ...(invoiceDate   ? { invoice_date:   invoiceDate   } : {}),
      ...(inventoryType === 'chemicals' ? {
        unit_quantity:  parseFloat(lot.quantity) || 0,
        total_quantity: parseFloat(((parseFloat(lot.quantity) || 0) * (parseFloat(data.item?.primary_unit_quantity) || 1)).toFixed(4)),
        ...docFields(docUploads),
      } : inventoryType !== 'products' ? {
        quantity: parseFloat(lot.quantity) || 0,
        ...docFields(docUploads),
      } : {
        ...(variantId ? { variant_id: variantId } : {}),
      }),
    };

    const { error: updateError } = await supabase
      .from(config.transactionsTable)
      .update(updatePayload)
      .eq('id', txId);
    if (updateError) throw updateError;
  }

  // Create inventory items per lot.
  // For multi-lot: always use JS insert (skip trigger check) so every lot gets its own row.
  // For single-lot: check if DB trigger already handled it, only insert via JS if not.
  if (inventoryType === 'chemicals') {
    const unitQty  = parseFloat(lot.quantity) || 0;
    const unitSize = parseFloat(data.item?.primary_unit_quantity) || 1;
    const totalQty = parseFloat((unitQty * unitSize).toFixed(4));
    const itemId   = data.item?.id;
    const locId    = location?.id;

    // Delete trigger-created items for the relevant transaction ID before JS insert
    if (isMultiLot && isFirstLot) {
      await supabase.from(config.itemsTable).delete()
        .eq('company_id', companyId).eq('source_transaction_id', txId);
    }
    if ((!isFirstLot && isMultiLot) || (isFirstLot && isPartial)) {
      await supabase.from(config.itemsTable).delete()
        .eq('company_id', companyId).eq('source_transaction_id', effectiveTxId);
    }

    // Single-lot full delivery: check if trigger already handled it
    if (!isMultiLot && !isPartial) {
      const { data: triggerCreated } = await supabase.from(config.itemsTable).select('id')
        .eq('company_id', companyId).eq('source_transaction_id', txId).maybeSingle();
      if (triggerCreated) return { success: true };
    }

    // Insert this lot's item via JS
    await supabase.from(config.itemsTable).delete()
      .eq('company_id', companyId).eq('raw_material_id', itemId)
      .eq('location_id', locId).eq('lot_number', lot.lotNumber || '').eq('batch_number', lot.batchNumber || '').eq('is_active', false);

    const { data: existing } = await supabase.from(config.itemsTable).select('*')
      .eq('company_id', companyId).eq('raw_material_id', itemId)
      .eq('location_id', locId).eq('lot_number', lot.lotNumber || '').eq('batch_number', lot.batchNumber || '').eq('is_active', true).maybeSingle();

    if (existing) {
      await supabase.from(config.itemsTable).update({
        current_unit_quantity:  existing.current_unit_quantity + unitQty,
        current_total_quantity: parseFloat((existing.current_total_quantity + totalQty).toFixed(4)),
        expiry_date:            expiryDate || existing.expiry_date,
        source_transaction_id:  effectiveTxId,
      }).eq('id', existing.id);
    } else {
      await supabase.from(config.itemsTable).insert([{
        company_id: companyId, raw_material_id: itemId, location_id: locId,
        lot_number: lot.lotNumber || '', batch_number: lot.batchNumber || '',
        current_unit_quantity: unitQty, original_unit_quantity: unitQty,
        current_total_quantity: totalQty, original_total_quantity: totalQty,
        expiry_date: expiryDate || null, is_active: true,
        source_transaction_id: effectiveTxId, last_updated_at: new Date().toISOString(),
      }]);
    }

    const { error: rpcErr } = await supabase.rpc('recalculate_raw_materials_aggregate_inventory', {
      p_company_id: companyId, p_raw_material_id: itemId, p_location_id: locId
    });
    if (rpcErr) await updateAggregate(companyId, config, itemId, locId);

  } else if (inventoryType !== 'products') {
    // Generic (caps/bottles/labels/supplies)
    const qty    = parseFloat(lot.quantity) || 0;
    const itemId = data.item?.id;
    const locId  = location?.id;

    // Delete trigger-created items for the relevant transaction ID before JS insert
    if (isMultiLot && isFirstLot) {
      await supabase.from(config.itemsTable).delete()
        .eq('company_id', companyId).eq('source_transaction_id', txId);
    }
    if ((!isFirstLot && isMultiLot) || (isFirstLot && isPartial)) {
      await supabase.from(config.itemsTable).delete()
        .eq('company_id', companyId).eq('source_transaction_id', effectiveTxId);
    }

    // Single-lot full delivery: check if trigger already handled it
    if (!isMultiLot && !isPartial) {
      const { data: triggerCreated } = await supabase.from(config.itemsTable).select('id')
        .eq('company_id', companyId).eq('source_transaction_id', txId).maybeSingle();
      if (triggerCreated) return { success: true };
    }

    // Insert this lot's item via JS
    await supabase.from(config.itemsTable).delete()
      .eq('company_id', companyId).eq(config.itemIdField, itemId)
      .eq('location_id', locId).eq('lot_number', lot.lotNumber || '').eq('batch_number', lot.batchNumber || '').eq('is_active', false);

    const { data: existing } = await supabase.from(config.itemsTable).select('*')
      .eq('company_id', companyId).eq(config.itemIdField, itemId)
      .eq('location_id', locId).eq('lot_number', lot.lotNumber || '').eq('batch_number', lot.batchNumber || '').eq('is_active', true).maybeSingle();

    if (existing) {
      await supabase.from(config.itemsTable).update({
        current_quantity: existing.current_quantity + qty,
        source_transaction_id: effectiveTxId, last_updated_at: new Date().toISOString(),
      }).eq('id', existing.id);
    } else {
      await supabase.from(config.itemsTable).insert([{
        company_id: companyId, [config.itemIdField]: itemId, location_id: locId,
        lot_number: lot.lotNumber || '', batch_number: lot.batchNumber || '',
        current_quantity: qty, original_quantity: qty,
        is_active: true, source_transaction_id: effectiveTxId, last_updated_at: new Date().toISOString(),
      }]);
    }

    await updateAggregate(companyId, config, itemId, locId);

  } else if (inventoryType === 'products') {
    const productId = data.item?.id;
    const locId     = location?.id;

    const uQty          = parseInt(lot.unitQty)    || 0;
    const cQty          = parseInt(lot.caseQty)    || 0;
    const pQty          = parseInt(lot.palletQty)  || 0;
    const unitsPerCase  = parseInt(lot._unitsPerCase)   || 1;
    const casesPerPallet= parseInt(lot._casesPerPallet) || 1;
    const enableCases   = lot._enableCases  ?? true;
    const enablePallets = lot._enablePallets ?? false;

    await supabase.from('product_inventory_items').insert([{
      company_id:               companyId,
      product_id:               productId,
      variant_id:               variantId || null,
      location_id:              locId,
      pallet_quantity:          pQty,
      case_quantity:            cQty,
      unit_quantity:            uQty,
      original_pallet_quantity: pQty,
      original_case_quantity:   cQty,
      original_unit_quantity:   uQty,
      units_per_case:           unitsPerCase,
      cases_per_pallet:         casesPerPallet,
      enable_cases:             enableCases,
      enable_pallets:           enablePallets,
      lot_number:               lot.lotNumber   || '',
      batch_number:             lot.batchNumber || '',
      manufactured_date:        lot.mfgDate     || null,
      expiry_date:              expiryDate      || null,
      source_transaction_id:    effectiveTxId,
      is_active:                true,
      last_updated_at:          new Date().toISOString(),
      // Documents — use uploaded docs for first lot, copied paths for subsequent lots
      ...(isFirstLot ? {
        ...(docUploads.po             ? { po_file_path:             docUploads.po.path,             po_file_name:             docUploads.po.name,             po_file_size:             docUploads.po.size }             : {}),
        ...(docUploads.invoice        ? { invoice_file_path:        docUploads.invoice.path,        invoice_file_name:        docUploads.invoice.name,        invoice_file_size:        docUploads.invoice.size }        : {}),
        ...(docUploads.coa            ? { coa_file_path:            docUploads.coa.path,            coa_file_name:            docUploads.coa.name,            coa_file_size:            docUploads.coa.size }            : {}),
        ...(docUploads.sds            ? { sds_file_path:            docUploads.sds.path,            sds_file_name:            docUploads.sds.name,            sds_file_size:            docUploads.sds.size }            : {}),
        ...(docUploads.bol            ? { bol_file_path:            docUploads.bol.path,            bol_file_name:            docUploads.bol.name,            bol_file_size:            docUploads.bol.size }            : {}),
        ...(docUploads.production_doc ? { production_doc_file_path: docUploads.production_doc.path, production_doc_file_name: docUploads.production_doc.name, production_doc_file_size: docUploads.production_doc.size } : {}),
      } : copiedDocPaths),
    }]);
  }

  return { success: true };
}

// ── Delete a PO transaction ───────────────────────────────────────────────────

const TYPE_TO_TABLE = {
  caps:      'caps_transactions',
  bottles:   'bottle_transactions',
  labels:    'label_transactions',
  supplies:  'supply_transactions',
  chemicals: 'raw_materials_transactions',
  products:  'product_inventory_transactions',
};

export async function deletePOTransaction(type, id) {
  const table = TYPE_TO_TABLE[type];
  if (!table) throw new Error(`Unknown type: ${type}`);
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw error;
}

// ── Fetch all PO transactions for Tab 2 ───────────────────────────────────────

const PO_TX_TABLES = [
  { table: 'caps_transactions',              type: 'caps',      itemJoin: 'caps:cap_id(name,sku)',               locJoin: 'location:location_id(name)' },
  { table: 'bottle_transactions',            type: 'bottles',   itemJoin: 'bottles:bottle_id(name,sku)',         locJoin: 'location:location_id(name)' },
  { table: 'label_transactions',             type: 'labels',    itemJoin: 'labels:label_id(name,sku)',           locJoin: 'location:location_id(name)' },
  { table: 'supply_transactions',            type: 'supplies',  itemJoin: 'supplies:supply_id(name,sku)',        locJoin: 'location:location_id(name)' },
  { table: 'raw_materials_transactions',     type: 'chemicals', itemJoin: 'raw_materials:raw_material_id(name,sku,primary_unit_type,primary_unit_measure,primary_unit_quantity)', locJoin: 'location:location_id(name)' },
  { table: 'product_inventory_transactions', type: 'products',  itemJoin: 'products:product_id(name,sku),variants:variant_id(id,name)', locJoin: 'to_location:to_location_id(name)' },
];

// ── Cleanup inventory items when a delivered transaction is deleted ───────────

export async function cleanupDeliveredTransaction(companyId, tx) {
  if (tx.delivery_status !== 'delivered') return;
  const config = getInventoryConfig(tx._type);
  if (!config?.itemsTable) return;

  await supabase.from(config.itemsTable).delete().eq('source_transaction_id', tx.id);

  const itemId = tx.raw_material_id || tx[config.itemIdField];
  const locId  = tx.location_id || tx.to_location_id;
  if (!itemId || !locId || tx._type === 'products') return;

  if (tx._type === 'chemicals') {
    const { error: rpcErr } = await supabase.rpc('recalculate_raw_materials_aggregate_inventory', {
      p_company_id: companyId, p_raw_material_id: itemId, p_location_id: locId
    });
    if (rpcErr) await updateAggregate(companyId, config, itemId, locId);
  } else {
    await updateAggregate(companyId, config, itemId, locId);
  }
}

// ── Fetch all PO transactions for Tab 2 ───────────────────────────────────────

export async function fetchPOTransactions(companyId) {
  const results = await Promise.all(
    PO_TX_TABLES.map(({ table, type, itemJoin, locJoin }) =>
      supabase.from(table)
        .select(`*, ${itemJoin}, ${locJoin}`)
        .eq('company_id', companyId)
        .eq('transaction_type', 'purchase')
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) console.error(`fetchPOTransactions [${table}]:`, error.message);
          return (data || []).map(tx => ({ ...tx, _type: type }));
        })
    )
  );
  return results.flat().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}
